import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT, TABLE_EDITABLE_CELL } from "const"
import { MtPackage, MtPackageCreate, MtPackageAllowed } from "server/models"
import { mtPackagesRoute } from "client/parts/routes"
import { StateType, deepCopy } from "client/utils"
import {
    StoreType,
    PackageList,
    PackageFilters,
    PackageSettings,
    PackageActiveCell,

    LoadAllAccounts,
    LoadAllPackages,
    LoadAllPackageAllowed,
    LoadPackageById,
    SortPackages,
    SavePackage,
    SaveAllowedPackages,

    setAccountList,
    setPackageList,
    setPackageIds,
    setPackageFilters,
    setPackageActiveCell,
    setPackageAllowed,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    TableSearchWrapper,
    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
} from "client/components"

import PackageSearch from "client/pages/search/packageSearch"
import Columns from "./columns"

const rowSize = "sm"
let prevActiveCell: PackageActiveCell = null

const List = () => {
    const packageList = useSelector((state: StoreType) => state.package.packageList)
    const packageIds = useSelector((state: StoreType) => state.package.packageIds)
    const packageAllowed = useSelector((state: StoreType) => state.package.packageAllowed)
    const sortColumn = useSelector((state: StoreType) => state.package.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.package.sortOrder)
    const filters = useSelector((state: StoreType) => state.package.filters)
    const activeCell = useSelector((state: StoreType) => state.package.activeCell)
    const reloadList = useSelector((state: StoreType) => state.package.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const clearActiveCell = () => {
        dispatch(setPackageActiveCell(null))
    }

    useEffect(() => {
        document.addEventListener("click", clearActiveCell)
        return () => document.removeEventListener("click", clearActiveCell)
    }, [])

    useEffect(() => {
        if (prevActiveCell !== null && prevActiveCell !== activeCell) {
            // save updated package data
            const { packageId } = prevActiveCell
            const updatedPackg = packageList[packageId]
            const allowedAccounts = packageAllowed[packageId] || []

            const newPackageData: MtPackageCreate = {
                name: updatedPackg.name,
                settings: JSON.stringify(updatedPackg.settingsParsed),
                maxUsers: updatedPackg.maxUsers,
                price: updatedPackg.price,
                status: updatedPackg.status,
                visibility: updatedPackg.visibility,
            }

            const allowedPackages: MtPackageAllowed[] = []
            allowedAccounts.forEach((accountId) => {
                allowedPackages.push({
                    accountId,
                    packageId,
                })
            })

            SavePackage(packageId, newPackageData, lang)
            .then(() => (
                LoadPackageById(packageId).then((packg) => {
                    const packageListCopy: PackageList = packageList ? deepCopy(packageList) : {}
                    packageListCopy[packageId] = packg
                    dispatch(setPackageList(packageListCopy))
                })
            ))
            SaveAllowedPackages(packageId, allowedPackages)
            .then((updatedAllowedAccounts) => {
                dispatch(setPackageAllowed({ [packageId]: updatedAllowedAccounts }))
            })
        }

        prevActiveCell = activeCell
    }, [activeCell])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadAllAccounts().then((res) => {
                dispatch(setAccountList(res))
            }),
            LoadAllPackages().then((res) => {
                dispatch(setPackageList(res))
            }),
            LoadAllPackageAllowed().then((res) => {
                dispatch(setPackageAllowed(res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortPackages(Object.values(packageList), sortColumn, sortOrder, filters)
        dispatch(setPackageIds(sortedIds))
    }, [packageList, sortColumn, sortOrder, filters])

    const onFilter = (newFilters: PackageFilters) => {
        dispatch(setPackageFilters(newFilters))
    }

    const columns = Columns()

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.packages}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <PackageSearch
                        id="packageList"
                        rightOffset
                        filters={filters}
                        onFilter={onFilter}
                    />
                    <Button
                        variant="success"
                        onClick={() => dispatch(addModal({ url: `${mtPackagesRoute}/${EDIT}/0` }))}
                    >
                        {lang.add}
                    </Button>
                </TableSearchWrapper>

                <TableWrapper>
                    <TableScroller>
                        <TableContentWrapper inverted>
                            <thead>
                                <TableRow
                                    inverted
                                    noHover
                                    size={rowSize}
                                >
                                    {
                                        columns.map((column) => (
                                            <TableHeaderCell
                                                key={column.field}
                                                cellWidth="250px"
                                                style={column.style || null}
                                            >
                                                <span>{column.header}</span>
                                            </TableHeaderCell>
                                        ))
                                    }
                                </TableRow>
                            </thead>

                            <tbody>
                                {
                                    packageIds.map((packageId) => {
                                        const packg = packageList[packageId]
                                        return packg && (
                                            <TableRow
                                                key={packageId}
                                                inverted
                                                noHover
                                                size={rowSize}
                                                style={{ width: `${100 / packageIds.length}%` }}
                                            >
                                                {
                                                    columns.map((column) => {
                                                        const isEditable = activeCell
                                                            && activeCell.packageId === packg.id
                                                            && activeCell.column === column.field

                                                        return (
                                                            <TableCell
                                                                key={column.field}
                                                                cellMinWidth="150px"
                                                                isEditable={isEditable}
                                                                className={TABLE_EDITABLE_CELL}
                                                                style={column.style || null}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    dispatch(setPackageActiveCell({
                                                                        packageId: packg.id,
                                                                        column: column.field as keyof MtPackage | keyof PackageSettings,
                                                                    }))
                                                                }}
                                                            >
                                                                <TableCellContentWrapper>
                                                                    {
                                                                        isEditable && column.focusedFormatter
                                                                            ? column.focusedFormatter(packg)
                                                                            : column.formatter
                                                                                ? column.formatter(packg)
                                                                                : packg[column.field as keyof MtPackage]
                                                                    }
                                                                </TableCellContentWrapper>
                                                            </TableCell>
                                                        )
                                                    })
                                                }
                                            </TableRow>
                                        )
                                    })
                                }
                            </tbody>
                        </TableContentWrapper>
                    </TableScroller>
                </TableWrapper>
            </PageContent>
        </Container>
    )
}
export default List
